import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Requirement, RequirementType, ScenarioConfig, ScenarioIssue, ScenarioTool } from './types';
import { moveItem, newRequirement, omitKeys, removeAt, storedFacts } from './scenarioDraft';
import { AutoUnchangedHint, ConditionEditor } from './ConditionEditor';
import {
  AddButton,
  IssueList,
  ItemRow,
  ListTextField,
  MultiSelectField,
  RowActions,
  SelectField,
  TextField,
} from './ui';

const REQUIREMENT_TYPES: RequirementType[] = [
  'fact_present',
  'fact_non_empty',
  'fact_in',
  'system_present',
  'tool_success',
  'condition',
];

interface Props {
  config: ScenarioConfig;
  tool: ScenarioTool;
  pointer: string;
  onChange: (requires: Requirement[]) => void;
  issues: ScenarioIssue[];
}

export const RequirementsEditor: React.FC<Props> = ({ config, tool, pointer, onChange, issues }) => {
  const { t } = useTranslation();
  const factOptions = config.facts.map(f => ({ value: f.name, label: f.name }));
  const update = (index: number, requirement: Requirement) =>
    onChange(tool.requires.map((r, i) => (i === index ? requirement : r)));

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.requirementsHint')}</p>
      {tool.requires.map((requirement, index) => {
        const ptr = `${pointer}/${index}`;
        return (
          <ItemRow key={index} className="space-y-2">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-[220px_1fr_auto]">
              <SelectField
                label={t('agents.scenario.requirementType')}
                value={requirement.type}
                options={REQUIREMENT_TYPES.map(type => ({
                  value: type,
                  label: t(`agents.scenario.requirementTypes.${type}`),
                }))}
                onChange={value => update(index, newRequirement(value as RequirementType, requirement))}
                issues={issues}
                pointer={`${ptr}/type`}
              />
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {'fact' in requirement && (
                  <SelectField
                    label={t('agents.scenario.fact')}
                    value={requirement.fact}
                    options={factOptions}
                    onChange={value => update(index, { ...requirement, fact: value })}
                    issues={issues}
                    pointer={`${ptr}/fact`}
                  />
                )}
                {requirement.type === 'fact_in' && (
                  <ListTextField
                    label={t('agents.scenario.allowedValues')}
                    hint={t('agents.scenario.commaSeparated')}
                    values={requirement.values}
                    onCommit={values => update(index, { ...requirement, values })}
                    issues={issues}
                    pointer={`${ptr}/values`}
                  />
                )}
                {requirement.type === 'system_present' && (
                  <SelectField
                    label={t('agents.scenario.systemValue')}
                    value={requirement.field}
                    options={[{ value: 'caller_number', label: t('agents.scenario.system.caller_number') }]}
                    onChange={() => update(index, { ...requirement, field: 'caller_number' })}
                    issues={issues}
                    pointer={`${ptr}/field`}
                  />
                )}
                {requirement.type === 'tool_success' && (
                  <>
                    <SelectField
                      label={t('agents.scenario.tool')}
                      value={requirement.tool}
                      options={config.tools
                        .filter(other => other.id !== tool.id)
                        .map(other => ({ value: other.id, label: other.title || other.id }))}
                      onChange={value => update(index, { ...requirement, tool: value })}
                      issues={issues}
                      pointer={`${ptr}/tool`}
                    />
                    <MultiSelectField
                      label={t('agents.scenario.unchangedFacts')}
                      values={requirement.unchanged}
                      options={storedFacts(config).map(f => ({ value: f.name, label: f.name }))}
                      onChange={values => update(index, { ...requirement, unchanged: values })}
                      issues={issues}
                      pointer={`${ptr}/unchanged`}
                    />
                    <AutoUnchangedHint config={config} tool={requirement.tool} />
                  </>
                )}
                {requirement.type === 'condition' && (
                  <p className="self-end pb-2 text-xs text-gray-500 dark:text-gray-400 md:col-span-2">
                    {t('agents.scenario.condition.hint')}
                  </p>
                )}
              </div>
              <div className="self-end pb-1">
                <RowActions
                  index={index}
                  count={tool.requires.length}
                  onMove={(from, to) => onChange(moveItem(tool.requires, from, to))}
                  onRemove={() => onChange(removeAt(tool.requires, index))}
                />
              </div>
            </div>
            {requirement.type === 'condition' && (
              <ConditionEditor
                config={config}
                tool={tool}
                value={requirement.when}
                onChange={when => update(index, { ...requirement, when })}
                issues={issues}
                pointer={`${ptr}/when`}
              />
            )}
            <TextField
              label={t('agents.scenario.blockMessage')}
              hint={t(
                requirement.type === 'condition' ? 'agents.scenario.blockMessageRequired' : 'agents.scenario.blockMessageHint'
              )}
              value={requirement.message ?? ''}
              onChange={value => {
                // A condition always keeps its message: the default text could not explain the rule.
                const rest = omitKeys(requirement, 'message');
                const keep = value || requirement.type === 'condition';
                update(index, (keep ? { ...rest, message: value } : rest) as Requirement);
              }}
              issues={issues}
              pointer={`${ptr}/message`}
            />
            <IssueList issues={issues} pointer={ptr} />
          </ItemRow>
        );
      })}
      <AddButton
        label={t('agents.scenario.addRequirement')}
        onClick={() => onChange([...tool.requires, newRequirement('fact_present')])}
      />
    </div>
  );
};
